import { useState } from "react";
import Select from "react-select";
import { COUNTRY_OPTIONS } from "../constants/countryOptions";
import { customSelectStyles } from "../CustomStyles/CustomSelect";

const normalizeSupplierName = (supplier) =>
  String(supplier?.supplier?.supplier || "").trim();

const ADD_NEW_SUPPLIER_VALUE = "__add_new_supplier__";

export default function FulfillmentSection({
  product,
  setProduct,
  suppliers,
  onCreateSupplier,
}) {
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState("");
  const [newSupplierEmail, setNewSupplierEmail] = useState("");
  const [newSupplierCity, setNewSupplierCity] = useState("");
  const [newSupplierProvince, setNewSupplierProvince] = useState("");
  const [newSupplierCountry, setNewSupplierCountry] = useState("Canada");
  const [newSupplierCurrency, setNewSupplierCurrency] = useState("CAD");
  const [newSupplierCategory, setNewSupplierCategory] =
    useState("Raw Materials");
  const [isSavingSupplier, setIsSavingSupplier] = useState(false);


  // landingCostOverride
  const [landingCostOverride, setLandingCostOverride] = useState(false);


  const isIngredientType = product.type === "ingredient";
  const showBackOrderField = !isIngredientType;
  const allowedBackOrder = isIngredientType
    ? "Supplier"
    : "Bill of Materials";
  const backOrderValue =
    product.backOrder === allowedBackOrder
      ? product.backOrder
      : allowedBackOrder;
  const showSupplierFields = isIngredientType;
  const supplierList = Array.isArray(suppliers) ? suppliers : [];
  const supplierOptions = supplierList
    .map((s) => {
      const name = normalizeSupplierName(s);
      if (!name) return null;
      return {
        value: name,
        label: name,
      };
    })
    .filter(Boolean);
  const addNewSupplierOption = {
    value: ADD_NEW_SUPPLIER_VALUE,
    label: "+ Add new supplier",
  };
  const finalSupplierOptions = [...supplierOptions, addNewSupplierOption];

  const selectedSupplierOption =
    supplierOptions.find(
      (opt) => opt.value === String(product.supplier || "").trim(),
    ) || null;

  const closeAddSupplierModal = () => {
    setShowAddSupplierModal(false);
    setNewSupplierName("");
    setNewSupplierEmail("");
    setNewSupplierCity("");
    setNewSupplierProvince("");
    setNewSupplierCountry("Canada");
    setNewSupplierCurrency("CAD");
    setNewSupplierCategory("Raw Materials");
  };

  const FieldLabel = ({ label, showRequired = true }) => (
    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
      {label}
      {showRequired && <span className="text-red-500">*</span>}
    </label>
  );

  const formatPriceToTwoDecimals = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    const numeric = Number(raw);
    if (Number.isNaN(numeric)) return "";
    return numeric.toFixed(2);
  };

  const submitNewSupplier = async () => {
    const trimmedName = newSupplierName.trim();
    const trimmedEmail = newSupplierEmail.trim();
    const trimmedCity = newSupplierCity.trim();
    const trimmedProvince = newSupplierProvince.trim();
    const trimmedCountry = newSupplierCountry.trim();
    const trimmedCurrency = String(newSupplierCurrency || "").trim();
    const trimmedCategory = String(newSupplierCategory || "").trim();

    if (
      !trimmedName ||
      !trimmedEmail ||
      !trimmedCity ||
      !trimmedProvince ||
      !trimmedCountry ||
      !trimmedCurrency ||
      !trimmedCategory ||
      isSavingSupplier
    )
      return;

    setIsSavingSupplier(true);
    try {
      const createdName = await onCreateSupplier?.({
        supplier: trimmedName,
        eMail: trimmedEmail,
        billingAddress: {
          city: trimmedCity,
          provinceState: trimmedProvince,
          country: trimmedCountry,
        },
        internationalTrade: {
          currency: trimmedCurrency,
          internationalTrade: false,
        },
        category: trimmedCategory,
        source: "manual",
      });
      if (createdName) {
        setProduct({ ...product, supplier: createdName });
      }
      closeAddSupplierModal();
    } finally {
      setIsSavingSupplier(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6 md:p-8 min-w-0 w-full max-w-full overflow-hidden">
      <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-4 sm:mb-6">FULFILLMENT</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {showBackOrderField && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Back Order
            </label>
            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
              value={backOrderValue}
              onChange={(e) =>
                setProduct({ ...product, backOrder: e.target.value })
              }
            >
              <option value={allowedBackOrder}>{allowedBackOrder}</option>
            </select>
          </div>
        )}

        {!isIngredientType && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Master case quantity (Kg)
            </label>
            <input
              type="number"
              min="1"
              placeholder="e.g. 1, 25"
              // placeholder="Auto-calculated from Recipe Builder"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              value={product.baseQuantity || ""}
              onChange={(e) =>
                setProduct({ ...product, baseQuantity: e.target.value })
              }
              // readOnly={true}
            />
          </div>
        )}

        {showSupplierFields && (
          <>
            <div>
              <FieldLabel label="Supplier" />
              <Select
                options={finalSupplierOptions}
                value={selectedSupplierOption}
                onChange={async (option) => {
                  const selectedValue = option?.value || "";
                  if (selectedValue === ADD_NEW_SUPPLIER_VALUE) {
                    setShowAddSupplierModal(true);
                    return;
                  }

                  setProduct({ ...product, supplier: selectedValue });
                }}
                placeholder="Select supplier"
                isSearchable
                maxMenuHeight={220}
                classNamePrefix="react-select"
                styles={customSelectStyles}
              />
            </div>

            <div>
              <FieldLabel label="SKU (Supplier)" />
              <input
                type="text"
                placeholder="Enter supplier SKU"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                value={product.supplierSku || ""}
                onChange={(e) =>
                  setProduct({ ...product, supplierSku: e.target.value })
                }
              />
            </div>

            <div>
              <FieldLabel label="Purchase price" />
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 5.25"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                value={product.purchasePrice ?? ""}
                onChange={(e) =>
                  setProduct({ ...product, purchasePrice: e.target.value })
                }
                onBlur={(e) =>
                  setProduct({
                    ...product,
                    purchasePrice: formatPriceToTwoDecimals(e.target.value),
                  })
                }
              />
            </div>

            {/* <div>
              <FieldLabel
                label={'Landing cost (additional "per unit" freight cost incurred)'}
              />

              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={landingCostOverride}
                  onChange={() => setLandingCostOverride(!landingCostOverride)}
                />
                <span className="text-sm text-gray-600">Override</span>
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 0"
              className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent 
    ${!landingCostOverride ? "bg-gray-100 cursor-not-allowed" : ""}`}
                value={product.landingCost ?? ""}
                disabled={!landingCostOverride}
                onChange={(e) =>
                  setProduct({ ...product, landingCost: e.target.value })
                }

                onBlur={(e) =>
                  setProduct({
                    ...product,
                    landingCost: formatPriceToTwoDecimals(e.target.value),
                  })
                }
              />
            </div> */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <FieldLabel
                  label={'Landing cost (additional "per unit" freight cost incurred)'}
                  showRequired={landingCostOverride}
                />

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={landingCostOverride}
                    onChange={() => setLandingCostOverride(!landingCostOverride)}
                  />
                  <span className="text-sm text-gray-600">Override</span>
                </div>
              </div>

              {/* Input */}
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 0"
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent 
      ${!landingCostOverride ? "bg-gray-100 cursor-not-allowed" : ""}`}
                value={product.landingCost ?? ""}
                disabled={!landingCostOverride}
                onChange={(e) =>
                  setProduct({ ...product, landingCost: e.target.value })
                }
                onBlur={(e) =>
                  setProduct({
                    ...product,
                    landingCost: formatPriceToTwoDecimals(e.target.value),
                  })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shipping days
              </label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 14"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                value={product.shippingDays || ""}
                onChange={(e) =>
                  setProduct({ ...product, shippingDays: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum order quantity
              </label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 100"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                value={product.minimumOrderQuantity || ""}
                onChange={(e) =>
                  setProduct({
                    ...product,
                    minimumOrderQuantity: e.target.value,
                  })
                }
              />
            </div>

            <div>
              <FieldLabel label="Order step quantity" />
              <input
                type="number"
                min="0"
                placeholder="e.g. 10"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                value={product.orderStepQuantity || ""}
                onChange={(e) =>
                  setProduct({ ...product, orderStepQuantity: e.target.value })
                }
              />
            </div>
          </>
        )}
      </div>

      {showAddSupplierModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl border border-gray-200">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Add new supplier
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Enter the supplier name to save it.
                </p>
              </div>
              <button
                type="button"
                onClick={closeAddSupplierModal}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                x
              </button>
            </div>

            <div className="mt-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supplier name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                autoFocus
                placeholder="e.g. ABC Trading"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                value={newSupplierName}
                onChange={(e) => setNewSupplierName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitNewSupplier();
                  }
                }}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-mail <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  placeholder="e.g. supplier@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  value={newSupplierEmail}
                  onChange={(e) => setNewSupplierEmail(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Laval"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  value={newSupplierCity}
                  onChange={(e) => setNewSupplierCity(e.target.value)}

                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country <span className="text-red-500">*</span>
                </label>
                <Select
                  options={COUNTRY_OPTIONS}
                  value={
                    COUNTRY_OPTIONS.find(
                      (opt) => opt.value === newSupplierCountry,
                    ) || COUNTRY_OPTIONS[0]
                  }
                  onChange={(option) =>
                    setNewSupplierCountry(option?.value || "Canada")
                  }
                  placeholder="Select country"
                  isSearchable
                  maxMenuHeight={200}
                  classNamePrefix="react-select"
                  styles={customSelectStyles}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Province/State <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Québec"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  value={newSupplierProvince}
                  onChange={(e) => setNewSupplierProvince(e.target.value)}
                />
              </div>



              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
                  value={newSupplierCurrency}
                  onChange={(e) => setNewSupplierCurrency(e.target.value)}
                >
                  <option value="CAD">CAD</option>
                  <option value="USD">USD</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
                  value={newSupplierCategory}
                  onChange={(e) => setNewSupplierCategory(e.target.value)}
                >
                  <option value="Raw Materials">Raw Materials</option>
                  <option value="Packaging Materials">Packaging Materials</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeAddSupplierModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                disabled={isSavingSupplier}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitNewSupplier}
                className="px-4 py-2 rounded-lg bg-black text-white hover:bg-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={
                  !newSupplierName.trim() ||
                  !newSupplierEmail.trim() ||
                  !newSupplierCity.trim() ||
                  !newSupplierProvince.trim() ||
                  !newSupplierCountry.trim() ||
                  !newSupplierCurrency.trim() ||
                  !newSupplierCategory.trim() ||
                  isSavingSupplier
                }
              >
                {isSavingSupplier ? "Saving..." : "Save supplier"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
